import { useEffect, useRef, useState, useCallback } from "react";
import { Replayer } from "rrweb";
import "rrweb/dist/style.css";
import { Play, Pause, RotateCcw } from "lucide-react";

export const SessionReplay = ({ events }) => {
  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const replayerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!stageRef.current || !events || events.length < 2) return;
    let cancelled = false;
    setReady(false); setFailed(false); setPlaying(false);

    const raf = requestAnimationFrame(() => {
      if (cancelled || !stageRef.current) return;
      stageRef.current.innerHTML = "";
      try {
        const meta = events.find((e) => e.type === 4) || {};
        const metaW = (meta.data && meta.data.width) || 0;
        const metaH = (meta.data && meta.data.height) || 0;
        const replayer = new Replayer(events, {
          root: stageRef.current,
          speed: 1,
          skipInactive: true,
          showWarning: false,
          mouseTail: false,
        });
        replayerRef.current = replayer;
        replayer.on("finish", () => setPlaying(false));

        const fit = () => {
          if (!stageRef.current || !wrapRef.current) return false;
          const wrapper = stageRef.current.querySelector(".replayer-wrapper");
          if (!wrapper) return false;
          const iframe = wrapper.querySelector("iframe");
          // Prefer the real recorded dimensions from the mounted iframe; fall back to meta.
          const recW = (iframe && iframe.offsetWidth) || metaW || 1024;
          const recH = (iframe && iframe.offsetHeight) || metaH || 640;
          if (!recW) return false;
          const cw = wrapRef.current.clientWidth || 452;
          // Always fit the recording to the panel width: shrink large (desktop) captures,
          // and gently up-scale narrow (mobile) ones so they fill the panel instead of
          // rendering at native size and overflowing. Capped to avoid blur.
          const scale = Math.min(1.3, cw / recW);
          wrapper.style.transform = `scale(${scale})`;
          wrapper.style.transformOrigin = "top left";
          stageRef.current.style.height = Math.round(recH * scale) + "px";
          return true;
        };
        // rrweb may mount the wrapper/iframe a frame or two after construction — retry until ready.
        let tries = 0;
        const tryFit = () => {
          if (cancelled) return;
          if (!fit() && tries < 30) { tries += 1; requestAnimationFrame(tryFit); }
        };
        tryFit();
        replayer.__fit = fit;
        window.addEventListener("resize", fit);
        setReady(true);
      } catch (e) {
        setFailed(true);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      const r = replayerRef.current;
      if (r) {
        try { window.removeEventListener("resize", r.__fit); } catch (_) {}
        try { r.pause(); } catch (_) {}
        try { r.destroy && r.destroy(); } catch (_) {}
      }
      replayerRef.current = null;
      if (stageRef.current) stageRef.current.innerHTML = "";
    };
  }, [events]);

  const toggle = useCallback(() => {
    const r = replayerRef.current;
    if (!r) return;
    if (playing) { r.pause(); setPlaying(false); return; }
    try {
      const t = r.getCurrentTime();
      const total = (r.getMetaData() || {}).totalTime || 0;
      r.play(t >= total - 50 ? 0 : t);
      setPlaying(true);
    } catch (e) { setFailed(true); setPlaying(false); }
  }, [playing]);

  const restart = useCallback(() => {
    const r = replayerRef.current;
    if (!r) return;
    try { r.play(0); setPlaying(true); }
    catch (e) { setFailed(true); setPlaying(false); }
  }, []);

  return (
    <div className="min-w-0 max-w-full">
      <div ref={wrapRef} data-testid="session-replay-player"
        className="rounded-2xl overflow-hidden border-2 border-ink bg-white max-w-full min-w-0">
        <div ref={stageRef} className="relative w-full max-w-full overflow-hidden" />
        {failed && <p className="p-4 text-sm font-600">Could not load this replay.</p>}
      </div>
      {ready && !failed && (
        <div className="flex items-center gap-2 mt-3">
          <button onClick={toggle} data-testid="replay-playpause"
            className="inline-flex items-center gap-2 text-sm font-800 px-4 py-2 rounded-xl border-2 border-ink bg-teal shadow-nb-sm hover:translate-y-[-1px] transition-transform">
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {playing ? "Pause" : "Play"}
          </button>
          <button onClick={restart} data-testid="replay-restart" title="Restart"
            className="inline-flex items-center gap-2 text-sm font-800 px-3 py-2 rounded-xl border-2 border-ink bg-white shadow-nb-sm hover:translate-y-[-1px] transition-transform">
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};
